import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchPropertySettings,
  updatePropertySettings,
  fetchGuestEngagement,
  updateGuestEngagement,
  sendTestGuestEngagement,
  fetchOnlineCheckout,
  updateOnlineCheckout,
  type UpdatePropertySettingsPayload,
  type GuestEngagementType,
  type UpdateGuestEngagementPayload,
  type UpsertOnlineCheckoutPayload,
} from '@/services/api/admin.api';

export function usePropertySettings(propertyId: string | null) {
  return useQuery({
    queryKey: ['admin', 'property-settings', propertyId],
    queryFn: () => fetchPropertySettings(propertyId as string),
    staleTime: 60_000,
    enabled: propertyId !== null,
  });
}

export function useUpdatePropertySettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ propertyId, payload }: { propertyId: string; payload: UpdatePropertySettingsPayload }) =>
      updatePropertySettings(propertyId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'property-settings'] });
    },
  });
}

export function useGuestEngagement(propertyId: string | null) {
  return useQuery({
    queryKey: ['admin', 'guest-engagement', propertyId],
    queryFn: () => fetchGuestEngagement(propertyId as string),
    staleTime: 60_000,
    enabled: propertyId !== null,
  });
}

export function useUpdateGuestEngagement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      propertyId,
      type,
      payload,
    }: {
      propertyId: string;
      type: GuestEngagementType;
      payload: UpdateGuestEngagementPayload;
    }) => updateGuestEngagement(propertyId, type, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'guest-engagement'] });
    },
  });
}

export function useSendTestGuestEngagement() {
  return useMutation({
    mutationFn: ({
      propertyId,
      type,
      to,
    }: {
      propertyId: string;
      type: GuestEngagementType;
      to: string;
    }) => sendTestGuestEngagement(propertyId, type, to),
  });
}

export function useOnlineCheckout(propertyId: string | null) {
  return useQuery({
    queryKey: ['admin', 'online-checkout', propertyId],
    queryFn: () => fetchOnlineCheckout(propertyId as string),
    staleTime: 60_000,
    enabled: propertyId !== null,
  });
}

export function useUpdateOnlineCheckout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      propertyId,
      payload,
    }: {
      propertyId: string;
      payload: UpsertOnlineCheckoutPayload;
    }) => updateOnlineCheckout(propertyId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'online-checkout'] });
    },
  });
}
